import React, {createContext, useCallback, useEffect} from "react";
import {useNavigate, useParams, useSearchParams} from "react-router-dom";
import {LoadingOverlay, Stack} from "@mantine/core";
import {useLocalStorage} from "@mantine/hooks";
import {chatApi} from "../chatApi";
import {useAppDispatch, useAppSelector} from "../../../app/hooks";
import classes from "./ChatView.module.css";
import {Thread} from "./Thread";
import {addMessage, clearThread, removeMessagesFromIndex} from "../chatSlice";
import {KAIResponseType, MessageWithMetadata} from "../../../types";
import {ChatSplashView} from "../components/ChatSplashView";

const systemName = "DIB";

const createEmptyMessage = (
    role: "user" | "assistant",
    position: number,
    responseType?: KAIResponseType
): MessageWithMetadata => {
    return {
        role,
        content: "",
        position,
        sources: [],
        isRegenerated: false,
        timestamp: new Date().toISOString(),
        thoughts: [],
        activities: [],
        pluginActivities: [],
        responseType,
    };
};

type ChatViewContextType = {
    regenerate: (
        messagePosition: number,
        responseType?: KAIResponseType
    ) => void;
    resend: (content: string, messagePosition: number) => void;
    sendMessage: (message: string) => void;
    followUpQuestionSubmit: (question: string) => void;
};

export const ChatViewContext = createContext<ChatViewContextType>({
    regenerate: () => {},
    resend: () => {},
    sendMessage: () => {},
    followUpQuestionSubmit: () => {},
});

const ChatView = () => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();

    const {threadId} = useParams<{threadId: string}>();
    const sliceThreadId = useAppSelector((state) => state.chat.threadId);
    const chosenResponseType = useAppSelector(
        (state) => state.session.chosenResponseType
    );
    const {isFetching} = chatApi.endpoints.getThread.useQuery(threadId || "", {
        refetchOnMountOrArgChange: true,
        skip: !threadId || threadId === "new",
    });

    const messages = useAppSelector((state) => state.chat.messages ?? []);
    const transientChat = useAppSelector((state) => state.transientChat);
    const realThreadId = threadId === "new" ? undefined : threadId;
    const threadFiltered =
        realThreadId === transientChat.threadId &&
        transientChat.error?.message.includes("content_filter");
    /* useAppSelector(
        (state) => state.chat.system?.name || "DIB"
    ); */
    const [sendMessage, {data, isLoading}] =
        chatApi.endpoints.sendMessage.useMutation();

    /* const [customChatCompletion] = useCustomChatCompletionMutation(); */

    const handleRegenerate = useCallback(
        (messagePosition: number, responseType?: KAIResponseType) => {
            if (!realThreadId) {
                return;
            }

            dispatch(removeMessagesFromIndex(messagePosition));
            dispatch(
                addMessage(
                    createEmptyMessage(
                        "assistant",
                        messagePosition,
                        responseType
                    )
                )
            );
            sendMessage({
                system: {name: systemName},
                threadId: realThreadId,
                messageId: messagePosition,
                responseType,
            });
        },
        [realThreadId, dispatch, sendMessage]
    );

    const handleResend = useCallback(
        (content: string, messagePosition: number) => {
            dispatch(removeMessagesFromIndex(messagePosition));
            dispatch(addMessage(createEmptyMessage("user", messagePosition)));
            dispatch(
                addMessage(createEmptyMessage("assistant", messagePosition + 1))
            );
            sendMessage({
                system: {name: systemName},
                threadId: realThreadId,
                messageId: messagePosition,
                message: content,
            });
        },
        [realThreadId, dispatch, sendMessage]
    );

    const handleSendMessage = useCallback(
        (message: string) => {
            if (!realThreadId) {
                dispatch(clearThread());
            }

            const userPosition = messages.length > 0 ? messages.length : 0;
            const assistantPosition =
                messages.length > 0 ? messages.length + 1 : 1;

            // Immediately add temporary messages to the thread
            dispatch(
                addMessage({
                    ...createEmptyMessage("user", userPosition),
                    content: message,
                })
            );
            dispatch(
                addMessage({
                    ...createEmptyMessage("assistant", assistantPosition),
                    responseType: chosenResponseType,
                })
            );

            sendMessage({
                system: {name: systemName},
                threadId: realThreadId,
                message,
                onThreadIdReceived: (threadId) => {
                    navigate(`/chat/${threadId}`, {
                        replace: true,
                        state: {from: "/"},
                    });
                },
            });
        },
        [
            navigate,
            messages.length,
            chosenResponseType,
            dispatch,
            realThreadId,
            sendMessage,
        ]
    );

    const handleFollowUpQuestionSubmit = useCallback(
        (question: string) => {
            handleSendMessage(question);
        },
        [handleSendMessage]
    );

    if (!threadId) {
        return <ChatSplashView />;
    }

    return (
        <ChatViewContext.Provider
            value={{
                regenerate: handleRegenerate,
                resend: handleResend,
                sendMessage: handleSendMessage,
                followUpQuestionSubmit: handleFollowUpQuestionSubmit,
            }}
        >
            <Stack className={classes.chatContainer} pos="relative">
                <Thread
                    id={realThreadId}
                    chatDisabled={isLoading || !!threadFiltered}
                />
                <LoadingOverlay
                    visible={isFetching && sliceThreadId !== realThreadId}
                    loaderProps={{type: "oval", color: "gneis.2", size: "md"}}
                />
            </Stack>
        </ChatViewContext.Provider>
    );
};

export default ChatView;
